import React, { useState, useEffect } from 'react';
import { Routes, Route, Navigate, useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Login from './pages/auth/Login';
import Register from './pages/auth/Register';
import ResetPassword from './pages/auth/ResetPassword';
import Dashboard from './pages/Dashboard';
import AuthGuard from './components/AuthGuard';
import PublicRouteRedirect from './components/PublicRouteRedirect';
import './App.css';
import { login } from './services/apiService';
import 'react-loading-skeleton/dist/skeleton.css'
import Campaigns from './pages/campaigns';
import CampaignWizard from './pages/campaignWizard';
import CampaignView from './pages/campaignView';
import LeadLists from './pages/leadLists';
import LeadListDetail from './pages/leadListDetail';
import Organizations from './pages/organizations';
import { ModalProvider } from './components/modal/ModalContext';
import Modal from './components/modal/Modal';
import LinkedInAccounts from './pages/linkedinAccounts';
import Messages from './pages/messages';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

function App() {
  const { t, i18n } = useTranslation();
  const [isAuthenticated, setIsAuthenticated] = useState(!!localStorage.getItem('authToken'));
  const navigate = useNavigate();
  const { lng } = useParams(); // Get language from URL parameters

  const [ready, setReady] = useState(i18n.isInitialized);

  const [isLoginLoading, setIsLoginLoading] = useState(false);
  const [loginError, setLoginError] = useState(null);
  const [loginData, setLoginData] = useState(null);

  useEffect(() => {
    const handleInit = () => setReady(true);
    if (!i18n.isInitialized) {
      i18n.on('initialized', handleInit);
    }
    return () => {
      i18n.off('initialized', handleInit);
    };
  }, []);

  useEffect(() => {
    if (i18n.isInitialized && lng && i18n.language !== lng && i18n.options.supportedLngs.includes(lng)) {
      i18n.changeLanguage(lng);
    }
    document.documentElement.dir = i18n.dir();
  }, [lng, i18n, i18n.language, i18n.isInitialized]);

  useEffect(() => {
    // Redirect to default language if no language prefix is present
    if (!lng && !window.location.pathname.startsWith('/ar') && !window.location.pathname.startsWith('/en')) {
      if(typeof i18n.language !== 'undefined' && !window.location.pathname.startsWith('/assets')) {
        navigate(`/${i18n.language}${window.location.pathname}`, { replace: true });
      }
    }
  }, [lng, navigate, i18n.language]);


  const handleLogin = (loginForm) => {
    login(loginForm, setIsLoginLoading, setLoginData, setLoginError);
  };
  const handleLogout = () => {
    localStorage.removeItem('authToken');
    setIsAuthenticated(false);
  };

  const changeLanguage = (newLng) => {
    const currentPath = window.location.pathname;
    const pathWithoutLang = currentPath.startsWith(`/en`) || currentPath.startsWith(`/ar`)
      ? currentPath.substring(3)
      : currentPath;

    i18n.changeLanguage(newLng);
    navigate(`/${newLng}${pathWithoutLang}`);
  };

  useEffect(()=>{
    if(loginData){
      localStorage.setItem('authToken', loginData?.accessToken);
      localStorage.setItem('user', JSON.stringify(loginData?.user));
      setIsAuthenticated(true);
    }
  },[loginData]);

  if(!i18n.language || typeof i18n.language === 'undefined' || !ready) return;

  return (
    <div className="App">
      <ModalProvider>
        <ToastContainer />
        <Routes>
          {/* Main language-prefixed routes */}
          <Route path="/:lng">
            {/* Default redirect based on auth */}
            <Route
              index
              element={
                isAuthenticated
                  ? <Navigate to={`/${i18n.language}/dashboard`} replace />
                  : <Navigate to={`/${i18n.language}/login`} replace />
              }
            />

            {/* Public routes */}
            <Route element={<PublicRouteRedirect isAuthenticated={isAuthenticated} i18n={i18n} />}>
              <Route path="login" element={<Login handleLogin={handleLogin} isLoginLoading={isLoginLoading} loginError={loginError} changeLanguage={changeLanguage} />} />
              <Route path="sign-up" element={<Register changeLanguage={changeLanguage} />} />
              <Route path="reset-password" element={<ResetPassword />} />
            </Route>

            {/* Protected routes */}
            <Route element={<AuthGuard isAuthenticated={isAuthenticated} handleLogout={handleLogout} i18n={i18n} changeLanguage={changeLanguage} />}>
              <Route path="dashboard" element={<Dashboard />} />
              <Route path="organizations" element={<Organizations />} />
              <Route path="linkedin-accounts" element={<LinkedInAccounts />} />
              <Route path="leads" element={<LeadLists />} />
              <Route path="leads/:listId" element={<LeadListDetail />} />
              <Route path="campaigns" element={<Campaigns />} />
              <Route path="campaigns/new" element={<CampaignWizard />} />
              <Route path="campaigns/:id/edit" element={<CampaignWizard />} />
              <Route path="campaigns/:id" element={<CampaignView />} />
              <Route path="messages" element={<Messages />} />
            </Route>

            {/* 404 */}
            <Route path="*" element={<h1>{t('404 Not Found')}</h1>} />
          </Route>

          {/* Redirect anything else to default language login */}
          <Route path="*" element={<Navigate to={`/${i18n.language}/login`} replace />} />
        </Routes>
        <Modal />
      </ModalProvider>
    </div>
  );
}

export default App;
